import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../Dropdown/Dropdown.css';
import LoadingOverlay from './LoadingOverlay';
import FotoAutenticada from './FotoAutenticada';
import BASE_URL from '../utils/api';
import { ehPrincipal, salvarPrincipal } from '../utils/responsavelPrincipal';

// Tela de entregas, a mesma para as duas: de serviço (pertence a um projeto) e de produto (pertence a uma venda).
const TIPOS = {
  servico: {
    titulo: 'Entregas de Serviços',
    endpoint: 'Entrega',
    fotosUpload: (id) => `Entrega/${id}/fotos`,
    fotoUrl: (id) => `${BASE_URL}/Entrega/fotos/${id}`,
    origem: 'Projeto',
    origemId: (e) => e.producoesId,
    clienteId: (e) => e.id_cliente,
    previsto: (e) => e.dataHora,
    realizado: (e) => e.dataConclusao,
    endereco: (e) => [e.localEntrega, e.numero, e.complemento].filter(Boolean).join(', '),
    concluida: (e) => /conclu/i.test(e.status || ''),
    aberta: (e) => !/conclu|cancel/i.test(e.status || ''),
    aviso: 'Agende quantas entregas forem necessárias para o projeto, em qualquer status dele: dá para ir entregando as partes do móvel e montar tudo depois. Projeto finalizado não aceita mais entregas.'
  },
  produto: {
    titulo: 'Entregas de Produtos',
    endpoint: 'EntregaProduto',
    fotosUpload: (id) => `EntregaProduto/${id}/Fotos`,
    fotoUrl: (id) => `${BASE_URL}/EntregaProduto/fotos/${id}`,
    origem: 'Venda',
    origemId: (e) => e.vendaId,
    clienteId: (e) => e.clienteId,
    previsto: (e) => e.dataProgramada,
    realizado: (e) => e.dataEntrega,
    endereco: (e) => [e.enderecoEntrega, e.cidadeEntrega, e.ufEntrega].filter(Boolean).join(', '),
    concluida: (e) => e.status === 'Entregue',
    aberta: (e) => e.status === 'Pendente' || e.status === 'Em_Transito',
    aviso: 'A primeira entrega nasce quando a venda de produto é aprovada. Para entregar em partes, diminua a quantidade dela e agende outra entrega para a mesma venda.'
  }
};

const apiCall = async (endpoint, method = 'GET', data = null) => {
  const config = { method, headers: { 'Content-Type': 'application/json' } };
  if (data) config.body = JSON.stringify(data);
  const res = await fetch(`${BASE_URL}/${endpoint}`, config);
  return tratarResposta(res);
};

const tratarResposta = async (res) => {
  const texto = await res.text();
  let corpo = null;
  try { corpo = texto ? JSON.parse(texto) : null; } catch { corpo = texto; }
  if (!res.ok) throw new Error(corpo?.mensagem || (typeof corpo === 'string' && corpo) || `Erro ${res.status}`);
  return corpo;
};

const dataIso = (d) => (d ? String(d).slice(0, 10) : '');
const dataBr = (d) => {
  const iso = dataIso(d);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : (d || '—');
};
const hojeIso = () => {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;
};
const statusLegivel = (s) => (s === 'Em_Transito' ? 'Em Trânsito' : (s || 'Agendada'));
const corStatus = (s) => (/conclu|entregue/i.test(s || '') ? { bg: '#e6f4ea', text: '#1e8e3e' } : /cancel/i.test(s || '') ? { bg: '#fdecea', text: '#d93025' } : { bg: '#fff3e0', text: '#b26a00' });

const campo = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, minWidth: 0 };
const entrada = { padding: 10, borderRadius: 6, border: '1px solid #ddd', fontSize: 14, fontWeight: 400, width: '100%', boxSizing: 'border-box' };
const botaoLaranja = { padding: '10px 18px', background: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const botaoCancelar = { padding: '10px 18px', background: '#fff', color: 'rgb(235, 133, 0)', border: '1px solid rgb(235, 133, 0)', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const fundo = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95010, padding: 16 };
const janela = (largura) => ({ width: '100%', maxWidth: largura, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 20, boxSizing: 'border-box' });
const grade = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 };

function Janela({ titulo, largura = 760, onFechar, children }) {
  return (
    <div style={fundo} onClick={onFechar}>
      <div style={janela(largura)} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>{titulo}</h3>
          <button type="button" onClick={onFechar} aria-label="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 34, height: 34, padding: 0, fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SimNao({ legenda, valor, onChange }) {
  return (
    <div style={campo}>
      <span>{legenda} *</span>
      <div style={{ display: 'flex', gap: 18, fontWeight: 400 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="radio" checked={valor === true} onChange={() => onChange(true)} /> Sim</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="radio" checked={valor === false} onChange={() => onChange(false)} /> Não</label>
      </div>
    </div>
  );
}

// Escolhe a quem a entrega pertence: uma entrega nunca existe solta
function SelecionarOrigem({ tipo, onEscolher, onFechar }) {
  const [itens, setItens] = useState(null);
  const [filtro, setFiltro] = useState('');

  useEffect(() => {
    const carregar = async () => {
      try {
        if (tipo === 'servico') {
          const lista = await apiCall('Producoes');
          setItens((Array.isArray(lista) ? lista : [])
            .filter(p => !/conclu|finaliz|cancel/i.test(p.status || ''))
            .map(p => ({ id: p.producaoId, titulo: p.titulo || p.oportunidadeTitulo, clienteNome: p.clienteNome, detalhe: `Contrato ${p.contratosId ?? '—'} · ${p.status || 'Pendente'}` })));
        } else {
          const lista = await apiCall('Vendas');
          setItens((Array.isArray(lista) ? lista : [])
            .filter(v => v.produtoId && v.estoqueReservado && !/cancel/i.test(v.statusVenda || ''))
            .map(v => ({ id: v.id, titulo: v.nomeProduto, clienteNome: v.nomeCliente || v.clienteNome, detalhe: `Quantidade ${v.quantidadeProduto ?? 1} · Entrega ${v.statusEntrega || 'Pendente'}` })));
        }
      } catch (err) {
        alert('Erro ao carregar: ' + err.message);
        setItens([]);
      }
    };
    carregar();
  }, [tipo]);

  const termo = filtro.toLowerCase().trim();
  const visiveis = (itens || []).filter(i => !termo || `${i.id} ${i.titulo || ''} ${i.clienteNome || ''}`.toLowerCase().includes(termo));

  return (
    <Janela titulo={tipo === 'servico' ? 'Para qual projeto é a entrega?' : 'Para qual venda de produto é a entrega?'} onFechar={onFechar}>
      <input style={{ ...entrada, marginBottom: 12 }} placeholder="Filtrar por ID, título ou cliente..." value={filtro} onChange={e => setFiltro(e.target.value)} />
      {itens === null ? <div style={{ padding: 30, textAlign: 'center', color: '#666' }}>Carregando...</div>
        : visiveis.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#666' }}>{tipo === 'servico' ? 'Nenhum projeto em andamento encontrado.' : 'Nenhuma venda de produto aprovada encontrada.'}</div>
        : visiveis.map(i => (
          <div key={i.id} onClick={() => onEscolher(i)} style={{ padding: 12, borderBottom: '1px solid #eee', cursor: 'pointer' }}>
            <strong style={{ color: 'rgb(235, 133, 0)' }}>#{i.id}</strong> {i.titulo || ''}
            <div style={{ fontSize: 13, color: '#666' }}>{i.clienteNome || 'Cliente não informado'} · {i.detalhe}</div>
          </div>
        ))}
    </Janela>
  );
}

function FormularioEntregaTela({ tipo, entrega, origem, onFechar, onSalvo }) {
  const cfg = TIPOS[tipo];
  const editando = !!entrega;
  const concluida = editando && cfg.concluida(entrega);
  const cancelada = editando && /cancel/i.test(entrega.status || '');
  const [form, setForm] = useState(() => (tipo === 'servico' ? {
    dataHora: dataIso(entrega?.dataHora), recebedorNome: entrega?.recebedorNome || '', recebedorDocumento: entrega?.recebedorDocumento || '',
    localEntrega: entrega?.localEntrega || '', numero: entrega?.numero || '', complemento: entrega?.complemento || '', cep: entrega?.cep || '',
    observacoes: entrega?.observacoes || '', dataConclusao: dataIso(entrega?.dataConclusao), itensEntregues: entrega?.itensEntregues || '',
    temMontagem: entrega?.temMontagem ?? null, entregasPendentes: entrega?.entregasPendentes ?? null
  } : {
    dataProgramada: dataIso(entrega?.dataProgramada), quantidadeEntregue: entrega?.quantidadeEntregue || '', recebedorNome: entrega?.recebedorNome || '', recebedorDocumento: entrega?.recebedorDocumento || '',
    entregadorNome: entrega?.entregadorNome || '', entregadorDocumento: entrega?.entregadorDocumento || '', enderecoEntrega: entrega?.enderecoEntrega || '',
    cidadeEntrega: entrega?.cidadeEntrega || '', ufEntrega: entrega?.ufEntrega || '', cepEntrega: entrega?.cepEntrega || '', complementoEntrega: entrega?.complementoEntrega || '',
    observacoes: entrega?.observacoes || '', dataEntrega: dataIso(entrega?.dataEntrega), entregasPendentes: entrega?.entregasPendentes ?? null, status: entrega?.status || 'Pendente'
  }));
  const [salvando, setSalvando] = useState(false);
  const [pessoas, setPessoas] = useState([]);
  const [responsaveis, setResponsaveis] = useState(entrega?.responsaveis || []);
  const [novosResponsaveis, setNovosResponsaveis] = useState([]);
  const [fotos, setFotos] = useState(entrega?.fotos || []);
  // Tarefa do projeto ligada à entrega (opcional): tarefa do tipo Montagem marca a entrega como montagem
  const [tarefaId, setTarefaId] = useState(entrega?.tarefaId ? String(entrega.tarefaId) : '');
  const [tarefas, setTarefas] = useState([]);
  const projetoId = entrega?.producoesId ?? origem?.id;

  useEffect(() => {
    if (tipo !== 'servico') return;
    apiCall('Pessoas').then(l => setPessoas(Array.isArray(l) ? l : [])).catch(() => setPessoas([]));
    apiCall('TarefasProjeto')
      .then(l => setTarefas((Array.isArray(l) ? l : []).filter(t => String(t.producaoId) === String(projetoId) && !/cancel/i.test(t.status || ''))))
      .catch(() => setTarefas([]));
  }, [tipo, projetoId]);

  const tarefaEscolhida = tarefas.find(t => String(t.id) === String(tarefaId));
  const ehMontagem = tarefaEscolhida ? /montagem/i.test(tarefaEscolhida.tipoTarefaNome || '') : entrega?.tipoEntrega === 1;

  const mudar = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const Texto = (rotulo, k, props = {}) => (
    <label style={campo}>{rotulo}<input style={entrada} value={form[k] ?? ''} onChange={e => mudar(k, e.target.value)} {...props} /></label>
  );

  const atualizarFotos = async () => {
    const atual = await apiCall(`${cfg.endpoint}/${entrega.id}`);
    setFotos(atual.fotos || []);
  };

  const enviarFotos = async (arquivos) => {
    const imagens = Array.from(arquivos || []).filter(a => a.type.startsWith('image/'));
    if (imagens.length === 0) { alert('Escolha uma ou mais imagens.'); return; }
    try {
      const dados = new FormData();
      imagens.forEach(a => dados.append('files', a));
      await tratarResposta(await fetch(`${BASE_URL}/${cfg.fotosUpload(entrega.id)}`, { method: 'POST', body: dados }));
      await atualizarFotos();
    } catch (err) {
      alert('Erro ao enviar as fotos: ' + err.message);
    }
  };

  const excluirFoto = async (foto) => {
    if (!window.confirm('Excluir esta foto?')) return;
    try {
      await tratarResposta(await fetch(cfg.fotoUrl(foto.id), { method: 'DELETE' }));
      await atualizarFotos();
    } catch (err) {
      alert('Erro ao excluir a foto: ' + err.message);
    }
  };

  const alternarPrincipal = async (r) => {
    try {
      await salvarPrincipal(apiCall, r.id, !ehPrincipal(r));
      const atual = await apiCall(`${cfg.endpoint}/${entrega.id}`);
      setResponsaveis(atual.responsaveis || []);
    } catch (err) {
      alert('Erro ao definir o responsável principal: ' + err.message);
    }
  };

  const salvar = async (e, cancelar = false) => {
    e?.preventDefault();
    if (!cancelada && !cancelar && !form[tipo === 'servico' ? 'dataHora' : 'dataProgramada']) { alert('Informe a data prevista da entrega.'); return; }
    try {
      setSalvando(true);
      const comStatus = cancelar ? { ...form, status: 'Cancelada' } : form;
      const corpo = tipo === 'servico'
        ? { ...comStatus, id: entrega?.id ?? 0, producoesId: entrega?.producoesId ?? origem.id, tarefaId: tarefaId ? parseInt(tarefaId, 10) : null }
        : {
            ...comStatus, vendaId: entrega?.vendaId ?? origem.id,
            dataProgramada: form.dataProgramada || null, dataEntrega: form.dataEntrega || null,
            quantidadeEntregue: parseInt(form.quantidadeEntregue, 10) || 0
          };

      let id = entrega?.id;
      if (editando) await apiCall(`${cfg.endpoint}/${id}`, 'PUT', corpo);
      else id = (await apiCall(cfg.endpoint, 'POST', corpo)).id;

      for (const pessoaId of novosResponsaveis) {
        await apiCall('Responsaveis', 'POST', { id: 0, pessoasId: parseInt(pessoaId, 10), entregaId: id, producaoId: entrega?.producoesId ?? origem?.id, id_cliente: entrega?.id_cliente ?? null, horasPrevistas: null, horasReais: null });
      }
      onSalvo();
    } catch (err) {
      alert('Erro ao salvar a entrega: ' + err.message);
    } finally {
      setSalvando(false);
    }
  };

  const origemId = editando ? cfg.origemId(entrega) : origem.id;

  return (
    <Janela titulo={`${editando ? `Editar Entrega #${entrega.id}` : 'Nova Entrega'} · ${cfg.origem} #${origemId}`} onFechar={onFechar}>
      <form onSubmit={salvar}>
        <div style={grade}>
          {tipo === 'servico' ? (<>
            <label style={{ ...campo, gridColumn: '1 / -1' }}>Tarefa do projeto (opcional)
              <select style={entrada} value={tarefaId} disabled={concluida} onChange={e => setTarefaId(e.target.value)}>
                <option value="">Nenhuma: entrega ligada só ao projeto</option>
                {tarefas.map(t => <option key={t.id} value={t.id}>#{t.id} - {t.descricao || 'Sem descrição'} ({t.tipoTarefaNome || 'sem tipo'})</option>)}
              </select>
              <span style={{ fontWeight: 400, fontSize: 12, color: ehMontagem ? 'rgb(184, 98, 0)' : '#666' }}>
                {ehMontagem ? '🔧 Entrega de montagem: a tarefa escolhida é do tipo Montagem.' : 'Tipo: Entrega. Escolha uma tarefa do tipo Montagem para marcar como montagem.'}
              </span>
            </label>
            {Texto('Data prevista *', 'dataHora', { type: 'date' })}
            {Texto('Nome do recebedor', 'recebedorNome')}
            {Texto('Documento do recebedor', 'recebedorDocumento')}
            {Texto('Local de entrega', 'localEntrega')}
            {Texto('Número', 'numero')}
            {Texto('Complemento', 'complemento')}
            {Texto('CEP', 'cep')}
          </>) : (<>
            {Texto('Data prevista *', 'dataProgramada', { type: 'date' })}
            {Texto('Quantidade', 'quantidadeEntregue', { type: 'number', min: 1, disabled: concluida, placeholder: editando ? '' : 'Tudo o que falta entregar' })}
            {Texto('Nome do recebedor', 'recebedorNome')}
            {Texto('Documento do recebedor', 'recebedorDocumento')}
            {Texto('Nome do entregador', 'entregadorNome')}
            {Texto('Documento do entregador', 'entregadorDocumento')}
            {Texto('Endereço', 'enderecoEntrega')}
            {Texto('Cidade', 'cidadeEntrega')}
            {Texto('UF', 'ufEntrega', { maxLength: 2 })}
            {Texto('CEP', 'cepEntrega')}
            {Texto('Complemento', 'complementoEntrega')}
            {!concluida && !cancelada && (
              <label style={campo}>Status
                <select style={entrada} value={form.status} onChange={e => mudar('status', e.target.value)}>
                  <option value="Pendente">Pendente</option>
                  <option value="Em_Transito">Em Trânsito</option>
                </select>
              </label>
            )}
          </>)}
          <label style={{ ...campo, gridColumn: '1 / -1' }}>Observações
            <textarea style={entrada} rows={2} value={form.observacoes} onChange={e => mudar('observacoes', e.target.value)} />
          </label>
        </div>

        {concluida && (
          <div style={{ marginTop: 16, padding: 14, background: '#e8f5e9', borderRadius: 8 }}>
            <strong style={{ color: '#2e7d32' }}>Dados da conclusão</strong>
            <div style={{ ...grade, marginTop: 10 }}>
              {tipo === 'servico' ? (<>
                {Texto('Data da entrega', 'dataConclusao', { type: 'date' })}
                <label style={{ ...campo, gridColumn: '1 / -1' }}>O que foi entregue
                  <textarea style={entrada} rows={2} value={form.itensEntregues} onChange={e => mudar('itensEntregues', e.target.value)} />
                </label>
                <SimNao legenda="Ainda há entregas pendentes?" valor={form.entregasPendentes} onChange={v => mudar('entregasPendentes', v)} />
                <SimNao legenda="Vai ter montagem?" valor={form.temMontagem} onChange={v => mudar('temMontagem', v)} />
              </>) : (<>
                {Texto('Data da entrega', 'dataEntrega', { type: 'date' })}
                <SimNao legenda="Ainda há entregas pendentes?" valor={form.entregasPendentes} onChange={v => mudar('entregasPendentes', v)} />
              </>)}
            </div>
          </div>
        )}

        {tipo === 'servico' && (
          <div style={{ marginTop: 16, padding: 14, background: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
            <strong style={{ color: 'rgb(235, 133, 0)' }}>Responsáveis</strong>
            {responsaveis.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontWeight: 600 }}>{r.nome || `Pessoa ${r.pessoasId}`}</span>
                <button type="button" onClick={() => alternarPrincipal(r)} style={{ padding: '3px 10px', borderRadius: 12, border: '1px solid rgb(235, 133, 0)', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: ehPrincipal(r) ? 'rgb(235, 133, 0)' : '#fff', color: ehPrincipal(r) ? '#fff' : 'rgb(235, 133, 0)' }}>
                  {ehPrincipal(r) ? '⭐ Principal' : '☆ Tornar principal'}
                </button>
              </div>
            ))}
            {novosResponsaveis.map((id, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <span>{pessoas.find(p => String(p.id) === String(id))?.nome || `Pessoa ${id}`}</span>
                <button type="button" onClick={() => setNovosResponsaveis(prev => prev.filter((_, x) => x !== i))} style={{ ...botaoCancelar, padding: '2px 10px' }}>Remover</button>
              </div>
            ))}
            <select style={{ ...entrada, marginTop: 10 }} value="" onChange={e => e.target.value && setNovosResponsaveis(prev => [...prev, e.target.value])}>
              <option value="">Adicionar responsável...</option>
              {pessoas.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
        )}

        {editando && (
          <div style={{ marginTop: 16, padding: 14, background: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
            <strong style={{ color: 'rgb(235, 133, 0)' }}>Fotos da entrega</strong>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' }}>
              {fotos.map(f => (
                <div key={f.id} style={{ position: 'relative' }}>
                  <FotoAutenticada src={cfg.fotoUrl(f.id)} alt={f.nomeArquivo || 'Foto da entrega'} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6 }} />
                  <button type="button" onClick={() => excluirFoto(f)} title="Excluir foto" style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, padding: 0, borderRadius: '50%', border: '2px solid #fff', background: '#f44336', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>×</button>
                </div>
              ))}
              {fotos.length === 0 && <span style={{ color: '#666', fontSize: 13 }}>Nenhuma foto ainda.</span>}
            </div>
            <input type="file" accept="image/*" multiple onChange={(e) => { const a = e.target.files; enviarFotos(a); e.target.value = ''; }} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
          <button type="submit" disabled={salvando} style={botaoLaranja}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          {editando && cfg.aberta(entrega) && (
            <button type="button" disabled={salvando} onClick={() => window.confirm('Cancelar esta entrega?') && salvar(null, true)} style={{ ...botaoLaranja, background: '#f44336' }}>Cancelar entrega</button>
          )}
          <button type="button" onClick={onFechar} style={botaoCancelar}>Fechar</button>
        </div>
      </form>
    </Janela>
  );
}

// Concluir a entrega: a foto é obrigatória. Ela comprova a entrega; a aprovação do projeto é outra coisa (aceite final do cliente).
function ConcluirEntrega({ tipo, entrega, entregas, onFechar, onConcluida }) {
  const cfg = TIPOS[tipo];
  const [dataEntrega, setDataEntrega] = useState(hojeIso());
  const [recebedorNome, setRecebedorNome] = useState(entrega.recebedorNome || '');
  const [recebedorDocumento, setRecebedorDocumento] = useState(entrega.recebedorDocumento || '');
  const [itensEntregues, setItensEntregues] = useState('');
  const [entregasPendentes, setEntregasPendentes] = useState(null);
  const [temMontagem, setTemMontagem] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const jaTemFoto = (entrega.fotos || []).length > 0;
  const outrasAbertas = entregas.filter(e => e.id !== entrega.id && String(cfg.origemId(e)) === String(cfg.origemId(entrega)) && cfg.aberta(e)).length;

  const concluir = async () => {
    if (arquivos.length === 0 && !jaTemFoto) { alert('A foto da entrega é obrigatória para concluir.'); return; }
    if (tipo === 'servico' && !itensEntregues.trim()) { alert('Informe o que foi entregue.'); return; }
    if (entregasPendentes === null) { alert('Informe se ainda há entregas pendentes.'); return; }
    if (tipo === 'servico' && temMontagem === null) { alert('Informe se vai ter montagem.'); return; }

    try {
      setEnviando(true);
      const dados = new FormData();
      dados.append(tipo === 'servico' ? 'DataConclusao' : 'DataEntrega', dataEntrega);
      dados.append('RecebedorNome', recebedorNome);
      dados.append('RecebedorDocumento', recebedorDocumento);
      dados.append('EntregasPendentes', entregasPendentes);
      if (tipo === 'servico') {
        dados.append('ItensEntregues', itensEntregues);
        dados.append('TemMontagem', temMontagem);
      }
      arquivos.forEach(a => dados.append('files', a));
      await tratarResposta(await fetch(`${BASE_URL}/${cfg.endpoint}/${entrega.id}/concluir`, { method: 'POST', body: dados }));
      alert(`Entrega #${entrega.id} concluída. ${entregasPendentes ? 'Ainda há entregas pendentes.' : 'Não há mais entregas pendentes.'}`);
      onConcluida();
    } catch (err) {
      alert('Erro ao concluir a entrega: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Janela titulo={`Concluir Entrega #${entrega.id}`} largura={620} onFechar={onFechar}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label style={campo}>Data da entrega *<input type="date" style={entrada} value={dataEntrega} onChange={e => setDataEntrega(e.target.value)} /></label>
        <div style={grade}>
          <label style={campo}>Nome do recebedor<input style={entrada} value={recebedorNome} onChange={e => setRecebedorNome(e.target.value)} /></label>
          <label style={campo}>Documento do recebedor<input style={entrada} value={recebedorDocumento} onChange={e => setRecebedorDocumento(e.target.value)} /></label>
        </div>
        {tipo === 'servico' && (
          <label style={campo}>O que foi entregue *
            <textarea style={entrada} rows={3} value={itensEntregues} onChange={e => setItensEntregues(e.target.value)} placeholder="Ex.: portas e laterais do armário. Isso aparece para o cliente na aprovação final." />
          </label>
        )}
        <label style={campo}>Foto da entrega {jaTemFoto ? `(já tem ${entrega.fotos.length})` : '*'}
          <input type="file" accept="image/*" multiple onChange={e => setArquivos(Array.from(e.target.files || []))} />
          <span style={{ fontWeight: 400, color: '#666', fontSize: 12 }}>A foto comprova a entrega. Ela não é a aprovação do projeto.</span>
        </label>
        <SimNao legenda={`Ainda há entregas pendentes?${outrasAbertas > 0 ? ` (${outrasAbertas} agendada(s) neste ${cfg.origem.toLowerCase()})` : ''}`} valor={entregasPendentes} onChange={setEntregasPendentes} />
        {tipo === 'servico' && <SimNao legenda="Vai ter montagem?" valor={temMontagem} onChange={setTemMontagem} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 20 }}>
        <button type="button" disabled={enviando} onClick={concluir} style={botaoLaranja}>{enviando ? 'Concluindo...' : 'Concluir entrega'}</button>
        <button type="button" onClick={onFechar} style={botaoCancelar}>Voltar</button>
      </div>
    </Janela>
  );
}

function Calendario({ tipo, entregas, onEscolherDia, onFechar }) {
  const cfg = TIPOS[tipo];
  const [mes, setMes] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const porDia = useMemo(() => {
    const mapa = {};
    entregas.filter(e => !/cancel/i.test(e.status || '')).forEach(e => {
      const dia = dataIso(cfg.previsto(e));
      if (dia) (mapa[dia] = mapa[dia] || []).push(e);
    });
    return mapa;
  }, [entregas, cfg]);

  const hoje = hojeIso();
  const inicio = new Date(mes.getFullYear(), mes.getMonth(), 1 - mes.getDay());
  const dias = Array.from({ length: 42 }, (_, i) => new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i));

  return (
    <Janela titulo={`Calendário de ${cfg.titulo}`} largura={860} onFechar={onFechar}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <button type="button" onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))} style={botaoCancelar}>←</button>
        <strong style={{ textTransform: 'capitalize' }}>{mes.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</strong>
        <button type="button" onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))} style={botaoCancelar}>→</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
        {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map(d => <div key={d} style={{ textAlign: 'center', fontWeight: 700, fontSize: 12, padding: 4 }}>{d}</div>)}
        {dias.map(d => {
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const doDia = porDia[iso] || [];
          const abertas = doDia.filter(cfg.aberta);
          const atrasado = abertas.length > 0 && iso < hoje;
          const dif = (new Date(`${iso}T00:00:00`) - new Date(`${hoje}T00:00:00`)) / 86400000;
          const vencendo = abertas.length > 0 && dif >= 0 && dif <= 3;
          return (
            <div key={iso} onClick={() => doDia.length > 0 && onEscolherDia(iso)} style={{ minHeight: 62, border: '1px solid #ddd', borderRadius: 4, padding: 4, background: d.getMonth() === mes.getMonth() ? '#fff' : '#f5f5f5', cursor: doDia.length > 0 ? 'pointer' : 'default' }}>
              <div style={{ fontSize: 12, color: d.getMonth() === mes.getMonth() ? '#111' : '#aaa' }}>{d.getDate()}</div>
              {doDia.length > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: atrasado ? '#f44336' : vencendo ? '#ef8f00' : '#333' }}>📦 {doDia.length}</div>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginTop: 12, fontSize: 12 }}>
        <span style={{ color: '#f44336', fontWeight: 700 }}>● Atrasado</span>
        <span style={{ color: '#ef8f00', fontWeight: 700 }}>● Vencendo (até 3 dias)</span>
      </div>
    </Janela>
  );
}

export default function TelaEntregas({ tipo }) {
  const cfg = TIPOS[tipo];
  const [entregas, setEntregas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [dia, setDia] = useState('');
  const [selecionandoOrigem, setSelecionandoOrigem] = useState(false);
  const [origemNova, setOrigemNova] = useState(null);
  const [editando, setEditando] = useState(null);
  const [concluindo, setConcluindo] = useState(null);
  const [calendario, setCalendario] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const lista = await apiCall(cfg.endpoint);
      setEntregas(Array.isArray(lista) ? lista.sort((a, b) => b.id - a.id) : []);
      setErro('');
    } catch (err) {
      setErro('Erro ao carregar as entregas: ' + err.message);
      setEntregas([]);
    } finally {
      setCarregando(false);
    }
  }, [cfg]);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = useMemo(() => {
    const termo = filtro.toLowerCase().trim();
    return entregas.filter(e => {
      if (dia && dataIso(cfg.previsto(e)) !== dia) return false;
      if (!termo) return true;
      const valores = {
        idEntrega: String(e.id), idOrigem: String(cfg.origemId(e) ?? ''), idCliente: String(cfg.clienteId(e) ?? ''),
        nomeCliente: (e.clienteNome || '').toLowerCase(), status: statusLegivel(e.status).toLowerCase(), data: `${dataBr(cfg.previsto(e))} ${dataIso(cfg.previsto(e))}`,
        tipo: tipo === 'servico' ? (e.tipoEntrega === 1 ? 'montagem' : 'entrega') : ''
      };
      return tipoFiltro === 'todos' ? Object.values(valores).some(v => v.includes(termo)) : (valores[tipoFiltro] || '').includes(termo);
    });
  }, [entregas, filtro, tipoFiltro, dia, cfg, tipo]);

  const aposSalvar = () => {
    setEditando(null);
    setOrigemNova(null);
    setConcluindo(null);
    carregar();
  };

  return (
    <div className="container-geral fundo-branco">
      <div className="entrega-aviso-automatica">🚚 {cfg.aviso}</div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
        <input style={{ ...entrada, maxWidth: 320 }} placeholder="Filtrar..." value={filtro} onChange={e => setFiltro(e.target.value)} />
        <select style={{ ...entrada, width: 'auto' }} value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)}>
          <option value="todos">Todos os campos</option>
          <option value="idEntrega">ID Entrega</option>
          <option value="idOrigem">ID {cfg.origem}</option>
          <option value="idCliente">ID Cliente</option>
          <option value="nomeCliente">Nome Cliente</option>
          <option value="status">Status</option>
          <option value="data">Data prevista</option>
          {tipo === 'servico' && <option value="tipo">Tipo (entrega ou montagem)</option>}
        </select>
        <button type="button" className="btn-adicionar-entrega" onClick={() => setSelecionandoOrigem(true)}>+ Agendar Entrega</button>
        <button type="button" onClick={() => setCalendario(true)} title="Ver calendário" style={{ fontSize: 28, background: 'transparent', border: 'none', cursor: 'pointer' }}>📅</button>
      </div>

      {dia && (
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          Entregas previstas para <strong>{dataBr(dia)}</strong> · <button type="button" onClick={() => setDia('')} style={{ ...botaoCancelar, padding: '2px 10px' }}>Ver todas</button>
        </div>
      )}

      {erro && <div style={{ textAlign: 'center', padding: 16, color: '#b00020', background: '#fdecea', borderRadius: 6 }}>{erro}</div>}

      {!carregando && !erro && (
        <div className="lista-entregas">
          <div className="entregas-header"><span>Total: {filtradas.length}</span></div>
          {filtradas.length === 0 ? (
            <div className="sem-entregas"><p>Nenhuma entrega encontrada</p><p>Clique em "+ Agendar Entrega" para criar.</p></div>
          ) : (
            <div className="entregas-container">
              {filtradas.map(e => {
                const cor = corStatus(e.status);
                return (
                  <div key={e.id} className="entrega-card">
                    <div className="entrega-card-content">
                      <div className="entrega-card-title">
                        <span className="entrega-icon">{tipo === 'servico' ? '🚚' : '📦'}</span>
                        <span className="entrega-id">#{e.id}</span>
                        <span className="entrega-cliente">{e.clienteNome || `Cliente #${cfg.clienteId(e) ?? '—'}`}</span>
                      </div>
                      <div className="entrega-card-grid">
                        <div className="entrega-card-field"><strong>{cfg.origem}:</strong> <span className="entrega-card-field-value">#{cfg.origemId(e) ?? '—'}{e.projetoTitulo ? ` - ${e.projetoTitulo}` : ''}</span></div>
                        {tipo === 'servico' && <div className="entrega-card-field"><strong>Tipo:</strong> <span className="entrega-card-field-value">{e.tipoEntrega === 1 ? '🔧 Montagem' : 'Entrega'}</span></div>}
                        {tipo === 'servico' && e.tarefaId && <div className="entrega-card-field"><strong>Tarefa:</strong> <span className="entrega-card-field-value">#{e.tarefaId}{e.tarefaDescricao ? ` - ${e.tarefaDescricao}` : ''}</span></div>}
                        <div className="entrega-card-field"><strong>Previsto:</strong> <span className="entrega-card-field-value">{dataBr(cfg.previsto(e))}</span></div>
                        <div className="entrega-card-field"><strong>Concluído:</strong> <span className="entrega-card-field-value">{cfg.realizado(e) ? dataBr(cfg.realizado(e)) : '—'}</span></div>
                        <div className="entrega-card-field"><strong>Status:</strong> <span style={{ padding: '2px 8px', borderRadius: 12, background: cor.bg, color: cor.text, fontWeight: 600 }}>{statusLegivel(e.status)}</span></div>
                        {cfg.endereco(e) && <div className="entrega-card-field entrega-endereco"><strong>Local:</strong> <span className="entrega-card-field-value">{cfg.endereco(e)}</span></div>}
                        {e.itensEntregues && <div className="entrega-card-field entrega-resumo"><strong>Entregue:</strong> <span className="entrega-card-field-value">{e.itensEntregues}</span></div>}
                        {tipo === 'produto' && <div className="entrega-card-field"><strong>Quantidade:</strong> <span className="entrega-card-field-value">{e.quantidadeEntregue}</span></div>}
                        {cfg.concluida(e) && e.entregasPendentes != null && <div className="entrega-card-field"><strong>Pendentes:</strong> <span className="entrega-card-field-value">{e.entregasPendentes ? 'Sim' : 'Não'}</span></div>}
                        {tipo === 'servico' && e.temMontagem != null && <div className="entrega-card-field"><strong>Montagem:</strong> <span className="entrega-card-field-value">{e.temMontagem ? 'Sim' : 'Não'}</span></div>}
                      </div>
                      <div className="entrega-card-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" className="btn-editar-entrega" onClick={() => setEditando(e)}>✏️ Editar</button>
                        {cfg.aberta(e) && <button type="button" className="btn-editar-entrega" onClick={() => setConcluindo(e)}>✅ Concluir</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selecionandoOrigem && (
        <SelecionarOrigem tipo={tipo} onFechar={() => setSelecionandoOrigem(false)} onEscolher={(o) => { setSelecionandoOrigem(false); setOrigemNova(o); }} />
      )}
      {(origemNova || editando) && (
        <FormularioEntregaTela key={editando?.id ?? 'nova'} tipo={tipo} entrega={editando} origem={origemNova} onFechar={() => { setEditando(null); setOrigemNova(null); carregar(); }} onSalvo={aposSalvar} />
      )}
      {concluindo && <ConcluirEntrega tipo={tipo} entrega={concluindo} entregas={entregas} onFechar={() => setConcluindo(null)} onConcluida={aposSalvar} />}
      {calendario && <Calendario tipo={tipo} entregas={entregas} onFechar={() => setCalendario(false)} onEscolherDia={(d) => { setDia(d); setCalendario(false); }} />}

      <LoadingOverlay isLoading={carregando} message="Carregando entregas..." />
    </div>
  );
}
